import { api } from '../api';
import { OfflineError } from '../errors/OfflineError';
import { ServerError } from '../errors/ServerError';

const EXPORT_CHUNK_SIZE = 1000;
const EXPORT_REQUEST_TIMEOUT_MS = 120000;
const EXPORT_MAX_ATTEMPTS = 3;
const EXPORT_RETRY_DELAY_MS = 1000;

interface ExportManifestType {
  apiKey: string;
  total?: number;
}

interface ExportManifest {
  snapshot: string;
  chunkSize: number;
  types: ExportManifestType[];
}

interface ExportChunk {
  data: unknown[];
  nextCursor: string | null;
}

export interface OrganizationExportProgress {
  phase: 'preparing' | 'downloading' | 'retrying';
  currentType?: string;
  recordsDownloaded: number;
  totalRecords: number | null;
  retryAttempt?: number;
}

interface OrganizationExportOptions {
  onProgress?: (progress: OrganizationExportProgress) => void;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function getErrorMessage(error: unknown): string {
  if (!error) {
    return 'Unknown error';
  }

  if (error instanceof OfflineError) {
    return 'No response from the server (network interruption or request timeout)';
  }

  const errorDetails = error as { message?: unknown; status?: unknown; contextId?: unknown };
  const details = [typeof errorDetails.message === 'string' ? errorDetails.message : String(error)];

  if (typeof errorDetails.status === 'number') {
    details.push(`status ${errorDetails.status}`);
  }

  if (typeof errorDetails.contextId === 'string') {
    details.push(`context ${errorDetails.contextId}`);
  }

  return details.join(', ');
}

function indentJSON(value: unknown, spaces: number): string {
  const indentation = ' '.repeat(spaces);
  const serialized = JSON.stringify(value, null, 2) ?? 'null';
  return serialized.replace(/\n/g, `\n${indentation}`);
}

async function requestWithRetries<T>(
  description: string,
  request: () => Promise<T>,
  onRetry?: (nextAttempt: number) => void
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= EXPORT_MAX_ATTEMPTS; attempt++) {
    try {
      return await request();
    } catch (error) {
      lastError = error;

      if (error instanceof ServerError && error.status && error.status < 500) {
        throw new Error(`${description} failed: ${getErrorMessage(error)}`);
      }

      if (attempt === EXPORT_MAX_ATTEMPTS) {
        break;
      }

      onRetry?.(attempt + 1);
      await sleep(EXPORT_RETRY_DELAY_MS * attempt);
    }
  }

  throw new Error(`${description} failed after ${EXPORT_MAX_ATTEMPTS} attempts: ${getErrorMessage(lastError)}`);
}

export async function fetchOrganizationExportBlob(
  orgId: string,
  options: OrganizationExportOptions = {}
): Promise<Blob> {
  if (!orgId) {
    throw new Error('An organization is required to export data');
  }

  options.onProgress?.({
    phase: 'preparing',
    recordsDownloaded: 0,
    totalRecords: null,
  });

  const organizationResponse = await requestWithRetries('Organization request', () => (
    api.get(`/organizations/${orgId}`, { timeout: EXPORT_REQUEST_TIMEOUT_MS })
  ));
  const manifestResponse = await requestWithRetries('Export manifest request', () => (
    api.get(`/organizations/${orgId}/sync-v2/manifest`, {
      params: { limit: EXPORT_CHUNK_SIZE, includeCounts: false },
      timeout: EXPORT_REQUEST_TIMEOUT_MS,
    })
  ));
  const manifest = manifestResponse.data as ExportManifest;
  const hasRecordCounts = manifest.types.every(type => typeof type.total === 'number');
  const totalRecords = hasRecordCounts
    ? manifest.types.reduce((total, type) => total + (type.total || 0), 0)
    : null;
  const blobParts: BlobPart[] = [
    '{\n  "organization": ',
    indentJSON(organizationResponse.data.organization ?? null, 2),
  ];
  let recordsDownloaded = 0;

  for (const type of manifest.types) {
    const seenCursors: { [cursor: string]: boolean } = {};
    let cursor: string | null = null;
    let hasRecords = false;

    blobParts.push(`,\n  ${JSON.stringify(type.apiKey)}: [`);

    do {
      const reportProgress = (phase: OrganizationExportProgress['phase'], retryAttempt?: number) => {
        options.onProgress?.({
          phase,
          currentType: type.apiKey,
          recordsDownloaded,
          totalRecords,
          retryAttempt,
        });
      };

      reportProgress('downloading');
      const chunkResponse = await requestWithRetries(
        `${type.apiKey} export`,
        () => api.get(`/organizations/${orgId}/sync-v2/chunk`, {
          params: {
            type: type.apiKey,
            snapshot: manifest.snapshot,
            cursor,
            limit: manifest.chunkSize,
          },
          timeout: EXPORT_REQUEST_TIMEOUT_MS,
        }),
        retryAttempt => reportProgress('retrying', retryAttempt)
      );
      const chunk = chunkResponse.data as ExportChunk;
      const chunkData = chunk.data || [];

      if (chunkData.length > 0) {
        const serializedChunk = chunkData
          .map(record => `    ${indentJSON(record, 4)}`)
          .join(',\n');
        blobParts.push(hasRecords ? ',\n' : '\n', serializedChunk);
        hasRecords = true;
        recordsDownloaded += chunkData.length;
      }

      if (chunk.nextCursor && seenCursors[chunk.nextCursor]) {
        throw new Error(`Data export stopped because the server repeated a cursor for ${type.apiKey}`);
      }

      if (chunk.nextCursor) {
        seenCursors[chunk.nextCursor] = true;
      }

      cursor = chunk.nextCursor;
    } while (cursor);

    blobParts.push(hasRecords ? '\n  ]' : ']');
  }

  options.onProgress?.({
    phase: 'preparing',
    recordsDownloaded,
    totalRecords,
  });
  blobParts.push('\n}\n');

  return new Blob(blobParts, { type: 'application/json' });
}
